import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import useEmblaCarousel from "embla-carousel-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { Gift, ChevronLeft, ChevronRight, Check, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import Navigation from "@/components/Navigation";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || "");

const AMOUNT_PRESETS = [25, 50, 100, 150];

const formSchema = z.object({
  recipientEmail: z.string().email("Please enter a valid email address"),
  recipientName: z.string().min(2, "Recipient name must be at least 2 characters"),
  amount: z.number().min(25, "Minimum amount is $25").max(500, "Maximum amount is $500"),
  message: z.string().optional(),
  purchaserEmail: z.string().email("Please enter a valid email address").optional().or(z.literal("")),
});

type FormData = z.infer<typeof formSchema>;

interface GiftCardDesign {
  id: string;
  name: string;
  imageUrl: string;
}

function BuyGiftCardForm() {
  const { toast } = useToast();
  const [selectedDesignIndex, setSelectedDesignIndex] = useState(0);
  const [customAmount, setCustomAmount] = useState("");
  const [showCustomAmount, setShowCustomAmount] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [paymentStep, setPaymentStep] = useState<"form" | "payment" | "success">("form");
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const { data: designsData, isLoading: isLoadingDesigns } = useQuery<{ success: boolean; designs: GiftCardDesign[] }>({
    queryKey: ["/api/cardivo/gift-cards/designs"],
  });

  const designs = designsData?.designs || [];

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      recipientEmail: "",
      recipientName: "",
      amount: 25,
      message: "",
      purchaserEmail: "",
    },
  });

  const createCheckoutMutation = useMutation({
    mutationFn: async (data: FormData & { designId: string }) => {
      const response = await apiRequest("POST", "/api/cardivo/gift-cards/checkout", data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create checkout session");
      }
      return response.json();
    },
    onSuccess: (data: any) => {
      if (data.clientSecret) {
        setClientSecret(data.clientSecret);
        setPaymentStep("payment");
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create checkout session",
        variant: "destructive",
      });
    },
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedDesignIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  const handleAmountPreset = (amount: number) => {
    form.setValue("amount", amount);
    setShowCustomAmount(false);
    setCustomAmount("");
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    const numericValue = parseFloat(value);
    if (!isNaN(numericValue) && numericValue >= 25 && numericValue <= 500) {
      form.setValue("amount", numericValue);
    }
  };

  const onSubmit = (data: FormData) => {
    if (designs.length === 0) {
      toast({
        title: "Error",
        description: "No gift card designs available",
        variant: "destructive",
      });
      return;
    }

    createCheckoutMutation.mutate({
      ...data,
      designId: designs[selectedDesignIndex].id,
    });
  };

  if (paymentStep === "payment" && clientSecret) {
    return (
      <>
        <Navigation />
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <PaymentForm
            clientSecret={clientSecret}
            onSuccess={() => setPaymentStep("success")}
            onBack={() => {
              setPaymentStep("form");
              setClientSecret("");
            }}
          />
        </Elements>
      </>
    );
  }

  if (paymentStep === "success") {
    return (
      <>
        <Navigation />
        <div className="container mx-auto max-w-2xl py-12 px-4">
          <Card className="bg-gradient-to-br from-purple-50 to-teal-50 dark:from-purple-950 dark:to-teal-950">
            <CardContent className="pt-8 text-center">
              <div className="mb-4 flex justify-center">
                <div className="rounded-full bg-green-100 p-4 dark:bg-green-900">
                  <Check className="h-12 w-12 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <h2 className="mb-2 text-2xl font-bold" data-testid="text-success-title">Gift Card Sent!</h2>
              <p className="mb-6 text-muted-foreground" data-testid="text-success-message">
                Your gift card has been sent to {form.getValues("recipientEmail")}
              </p>
              <Button
                onClick={() => {
                  setPaymentStep("form");
                  setClientSecret("");
                  form.reset();
                }}
                data-testid="button-buy-another"
              >
                Buy Another Gift Card
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="container mx-auto max-w-4xl py-8 px-4">
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-4xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent" data-testid="text-page-title">
            Gift a SnuggleCrafters Experience
          </h1>
          <p className="text-muted-foreground" data-testid="text-page-subtitle">
            Share the magic of personalized bedtime stories
          </p>
        </div>

      {isLoadingDesigns ? (
        <div className="flex justify-center py-12" data-testid="loading-designs">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div className="mb-8">
          <h2 className="mb-4 text-xl font-semibold" data-testid="text-design-title">Choose a Design</h2>
          
          <div className="relative">
            <div className="overflow-hidden" ref={emblaRef} data-testid="carousel-designs">
              <div className="flex">
                {designs.map((design, index) => (
                  <div key={design.id} className="flex-[0_0_100%] min-w-0 px-2 md:flex-[0_0_50%] lg:flex-[0_0_33.333%]">
                    <Card 
                      className={`overflow-hidden transition-all cursor-pointer hover:scale-105 ${
                        index === selectedDesignIndex 
                          ? "ring-2 ring-purple-600 shadow-lg" 
                          : "opacity-70"
                      }`}
                      onClick={() => emblaApi?.scrollTo(index)}
                      data-testid={`card-design-${index}`}
                    >
                      <div className="relative aspect-[3/2] bg-gradient-to-br from-purple-100 to-teal-100 dark:from-purple-900 dark:to-teal-900">
                        <img
                          src={design.imageUrl}
                          alt={design.name}
                          className="h-full w-full object-cover"
                          data-testid={`img-design-${index}`}
                        />
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                          <Gift className="mb-2 h-12 w-12" data-testid={`icon-gift-${index}`} />
                          <div className="text-2xl font-bold" data-testid={`text-amount-${index}`}>
                            ${form.watch("amount")}
                          </div>
                          <div className="mt-2 text-sm" data-testid={`text-recipient-${index}`}>
                            {form.watch("recipientName") || "Recipient Name"}
                          </div>
                          <div className="mt-1 text-xs opacity-75" data-testid={`text-cardnumber-${index}`}>
                            **** **** **** ****
                          </div>
                        </div>
                      </div>
                      <CardContent className="p-3 text-center">
                        <p className="text-sm font-medium" data-testid={`text-designname-${index}`}>{design.name}</p>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
            
            {designs.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={scrollPrev}
                  data-testid="button-carousel-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={scrollNext}
                  data-testid="button-carousel-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
          
          <div className="mt-4 flex justify-center gap-2">
            {designs.map((_, index) => (
              <button
                key={index}
                className={`h-2 w-2 rounded-full transition-all ${
                  index === selectedDesignIndex 
                    ? "w-8 bg-purple-600" 
                    : "bg-gray-300 dark:bg-gray-600"
                }`}
                onClick={() => emblaApi?.scrollTo(index)}
                data-testid={`button-dot-${index}`}
              />
            ))}
          </div>
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle data-testid="text-form-title">Gift Card Details</CardTitle>
              <CardDescription data-testid="text-form-description">
                Enter the recipient's information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="recipientEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Recipient's Email *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="recipient@example.com" 
                        {...field}
                        data-testid="input-recipient-email"
                      />
                    </FormControl>
                    <FormDescription>
                      The gift card will be sent to this email address
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="recipientName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Recipient's Name *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="John Doe" 
                        {...field}
                        data-testid="input-recipient-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gift Card Amount *</FormLabel>
                    <FormControl>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {AMOUNT_PRESETS.map((amount) => (
                            <Button
                              key={amount}
                              type="button"
                              variant={field.value === amount && !showCustomAmount ? "default" : "outline"}
                              onClick={() => handleAmountPreset(amount)}
                              data-testid={`button-amount-${amount}`}
                            >
                              ${amount}
                            </Button>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            min="25"
                            max="500"
                            placeholder="Custom amount"
                            value={customAmount}
                            onChange={(e) => {
                              handleCustomAmountChange(e.target.value);
                              setShowCustomAmount(true);
                            }}
                            data-testid="input-custom-amount"
                          />
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Minimum: $25 | Maximum: $500
                        </p>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Personal Message (Optional)</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Add a personal message to your gift..." 
                        className="resize-none"
                        {...field}
                        data-testid="textarea-message"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="purchaserEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Email (Optional)</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="your@example.com" 
                        {...field} 
                        data-testid="input-purchaser-email"
                      />
                    </FormControl>
                    <FormDescription>
                      Receive a confirmation of your purchase
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button
              type="submit"
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700"
              disabled={createCheckoutMutation.isPending || designs.length === 0}
              data-testid="button-proceed-payment"
            >
              {createCheckoutMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Proceed to Payment
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
      </div>
    </>
  );
}

function PaymentForm({ 
  clientSecret, 
  onSuccess, 
  onBack 
}: { 
  clientSecret: string; 
  onSuccess: () => void; 
  onBack: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const issueGiftCardMutation = useMutation({
    mutationFn: async (paymentIntentId: string) => {
      const response = await apiRequest("POST", "/api/cardivo/gift-cards/issue", { paymentIntentId });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to issue gift card");
      }
      return response.json();
    },
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to issue gift card",
        variant: "destructive",
      });
      setIsProcessing(false);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + "/gift-subscription",
        },
        redirect: "if_required",
      });

      if (error) {
        toast({
          title: "Payment Failed",
          description: error.message,
          variant: "destructive",
        });
        setIsProcessing(false);
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        issueGiftCardMutation.mutate(paymentIntent.id);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  return (
    <div className="container mx-auto max-w-2xl py-8 px-4">
      <Card>
        <CardHeader>
          <CardTitle data-testid="text-payment-title">Complete Your Purchase</CardTitle>
          <CardDescription data-testid="text-payment-description">
            Enter your payment information to send the gift card
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <PaymentElement />
            
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onBack}
                disabled={isProcessing}
                data-testid="button-back"
              >
                Back
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700"
                disabled={!stripe || isProcessing}
                data-testid="button-submit-payment"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing Payment...
                  </>
                ) : (
                  "Complete Purchase"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default function GiftSubscriptionPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 dark:from-purple-950 dark:via-gray-900 dark:to-teal-950">
      <BuyGiftCardForm />
    </div>
  );
}
